import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import * as React from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import PatientServices from "Services/PatientServices/PatientServices";
import useLang from "Shared/hooks/useLanguage";
import { useLoader } from "Shared/Loader/LoaderContext";
import { AddNewInputs } from "./AddNewInputs";

interface ApiDataModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    modalData: any;
    loadData?: (reset: boolean) => void;
    itemId?: number;
    tabIdToSend?: string | number;
}

interface SaveConfig {
    URL: string;
    APIMethod?: string;
    JSONRequest?: Record<string, any>;
    Column?: string;
    ActionTypeId?: number;
}

interface HeaderConfig {
    columnLabel: string;
    columnKey: string;
    isShowOnUi: boolean;
    isIndividualEditable: boolean;
    isRequired?: boolean;
    filterColumnsType?: string;
    inputType?: string;
    jsonOptionData?: string;
}

interface DropdownOption {
    value: string | number;
    label: string;
}

function ApiDataModal({
    openModal,
    setOpenModal,
    modalData,
    loadData,
    itemId,
    tabIdToSend,
}: ApiDataModalProps) {
    const { t } = useLang();
    const { showLoader, hideLoader } = useLoader();

    const [editableData, setEditableData] = React.useState<any[]>([]);
    const [saveConfig, setSaveConfig] = React.useState<SaveConfig | null>(null);

    // Initialize editable data and save config when modal opens
    React.useEffect(() => {
        if (modalData?.data?.data) {
            setEditableData([...modalData.data.data]);
        }

        if (modalData?.data?.saveQCURL) {
            try {
                const parsedConfig = JSON.parse(modalData.data.saveQCURL);
                if (Array.isArray(parsedConfig) && parsedConfig.length > 0) {
                    setSaveConfig(parsedConfig[0]);
                }
            } catch (error) {
                console.error("Failed to parse saveQCURL:", error);
            }
        }
    }, [modalData]);

    const handleClose = React.useCallback(() => {
        setOpenModal(false);
        setEditableData([]);
        setSaveConfig(null);
    }, [setOpenModal]);

    const handleFieldChange = React.useCallback(
        (rowIndex: number, columnKey: string, value: any) => {
            setEditableData((prevData) => {
                const updatedData = [...prevData];
                updatedData[rowIndex] = {
                    ...updatedData[rowIndex],
                    [columnKey]: value,
                };
                return updatedData;
            });
        },
        []
    );

    const getRequiredColumns = (): HeaderConfig[] => {
        const tabHeaders = modalData?.data?.tabHeaders as HeaderConfig[] | undefined;
        if (!tabHeaders) return [];
        return tabHeaders.filter(
            (h) => h.isShowOnUi && h.isRequired === true
        );
    };

    const isValueEmpty = (value: any, header: HeaderConfig): boolean => {
        if (value == null) return true;
        if (typeof value === "string" && value.trim() === "") return true;
        if (header.filterColumnsType === "dropdown" && (value === 0 || value === "0")) return true;
        return false;
    };

    const validateRequiredFields = (): { valid: boolean; missingLabels: string[] } => {
        const requiredColumns = getRequiredColumns();
        if (requiredColumns.length === 0) return { valid: true, missingLabels: [] };

        const missingLabels = new Set<string>();
        for (let rowIndex = 0; rowIndex < editableData.length; rowIndex++) {
            const row = editableData[rowIndex];
            for (const header of requiredColumns) {
                const value = row[header.columnKey];
                if (isValueEmpty(value, header)) {
                    missingLabels.add(header.columnLabel);
                }
            }
        }
        return {
            valid: missingLabels.size === 0,
            missingLabels: Array.from(missingLabels),
        };
    };

    const handleSave = async () => {
        if (!saveConfig) {
            toast.error(t("Save configuration is missing"));
            return;
        }

        const { valid, missingLabels } = validateRequiredFields();
        if (!valid) {
            const labels = missingLabels.join(", ");
            toast.error(t("Please fill required field(s)") + ": " + labels);
            return;
        }

        try {
            showLoader();

            const jsonRequest = saveConfig.JSONRequest || {};
            const correctiveActions = editableData
                .map((row: any) => row.CorrectiveActionReasonId)
                .filter((value: any) => value != null);

            const payload = {
                ids: itemId ? [itemId] : jsonRequest.ids || [],
                tabId: tabIdToSend || jsonRequest.tabId || 0,
                columns: saveConfig.Column || jsonRequest.columns || "",
                actionTypeId: saveConfig.ActionTypeId || 0,
                correctiveActionReasonId: correctiveActions[0] ?? null,
            };

            const response = await PatientServices.makeApiCallForDynamicGrid(
                saveConfig.URL,
                saveConfig.APIMethod || "POST",
                payload
            );

            if (response?.data?.statusCode === 200) {
                toast.success(
                    t(response.data.responseMessage || "Data saved successfully")
                );

                if (loadData) {
                    loadData(false);
                }

                handleClose();
            } else {
                toast.error(t(response?.data?.responseMessage || "Failed to save data"));
            }
        } catch (error: any) {
            console.error("Error saving data:", error);
            toast.error(
                t(error?.response?.data?.responseMessage || "Failed to save data")
            );
        } finally {
            hideLoader();
        }
    };

    const parseDropdownOptions = (dropdownValues: string): DropdownOption[] => {
        try {
            return JSON.parse(dropdownValues);
        } catch (error) {
            console.error("Failed to parse dropdown values:", error);
            return [];
        }
    };

    const parseJsonOptionData = (jsonOptionData: string): DropdownOption[] => {
        try {
            return JSON.parse(jsonOptionData);
        } catch (error) {
            console.error("Failed to parse jsonOptionData:", error);
            return [];
        }
    };

    const getCellDisplayValue = (
        cellValue: any,
        header: HeaderConfig,
        dropdownOptions: DropdownOption[]
    ): string => {
        // Handle jsonOptionData for dropdown fields
        if (header.jsonOptionData && header.inputType === "dropdown") {
            const parsedData = parseJsonOptionData(header.jsonOptionData);
            const match = parsedData.find((item) => item.value === cellValue);
            return match?.label || "";
        }

        // Handle numeric values with dropdown options
        if (
            (typeof cellValue === "number" || !isNaN(Number(cellValue))) &&
            dropdownOptions.length > 0
        ) {
            const match = dropdownOptions.find((option) => option.value == cellValue);
            if (match) {
                return match.label;
            }
        }

        return cellValue;
    };

    const renderTable = () => {
        const data = modalData?.data;

        if (!data?.tabHeaders || !data?.data || data.data.length === 0) {
            return <div className="text-center p-5">{t("No data available")}</div>;
        }

        const { tabHeaders, dropdownValues } = data;
        const dropdownOptions = dropdownValues
            ? parseDropdownOptions(dropdownValues)
            : [];

        const visibleHeaders = tabHeaders.filter(
            (header: HeaderConfig) => header.isShowOnUi
        );

        return (
            <div className="table_bordered overflow-hidden">
                <TableContainer
                    sx={{
                        maxHeight: "calc(100vh - 100px)",
                        "&::-webkit-scrollbar": {
                            width: 7,
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: "#fff",
                        },
                        "&:hover": {
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "var(--kt-gray-400)",
                                borderRadius: 2,
                            },
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "var(--kt-gray-400)",
                            borderRadius: 2,
                        },
                    }}
                    component={Paper}
                    className="shadow-none"
                >
                    <Table
                        aria-label="sticky table collapsible"
                        className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-1"
                        style={{ borderRadius: "8px", overflow: "hidden" }}
                    >
                        <TableHead className="h-40px">
                            <TableRow style={{ backgroundColor: "#f5f8fa" }}>
                                {visibleHeaders.map((header: HeaderConfig, index: number) => (
                                    <TableCell
                                        key={index}
                                        className="fw-semibold text-dark border-bottom border-top-0"
                                        style={{
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            backgroundColor: "#f5f8fa",
                                        }}
                                    >
                                        {header.columnLabel}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {editableData.map((row: any, rowIndex: number) => (
                                <TableRow key={rowIndex} className="bg-white">
                                    {visibleHeaders.map((header: HeaderConfig, colIndex: number) =>
                                        header.isIndividualEditable ? (
                                            <AddNewInputs
                                                key={colIndex}
                                                item={row}
                                                column={header}
                                                columnHeaderIndex={colIndex}
                                                onChange={(name: string, value: any) =>
                                                    handleFieldChange(rowIndex, name, value)
                                                }
                                                externalDropdownOptions={dropdownOptions}
                                            />
                                        ) : (
                                            <TableCell
                                                key={colIndex}
                                                style={{ padding: "12px 16px" }}
                                            >
                                                <span className="text-dark">
                                                    {getCellDisplayValue(
                                                        row[header.columnKey],
                                                        header,
                                                        dropdownOptions
                                                    ) || "-"}
                                                </span>
                                            </TableCell>
                                        )
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        );
    };

    const modalTitle = modalData?.data?.popupHeader || t("Data Details");

    const showSaveButton = React.useMemo(() => {
        const tabHeaders = modalData?.data?.tabHeaders;
        if (!tabHeaders) return false;

        const visibleHeaders = tabHeaders.filter(
            (header: HeaderConfig) => header.isShowOnUi
        );
        return visibleHeaders.some((item: HeaderConfig) => item.isIndividualEditable);
    }, [modalData]);

    return (
        <Modal
            show={openModal}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            size="lg"
            centered
            dialogClassName="custom-qc-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
                <h4 className="mb-0 fw-bold">{modalTitle}</h4>
            </Modal.Header>
            <Modal.Body className="px-4 py-3">{renderTable()}</Modal.Body>

            <Modal.Footer className="border-0 pt-2 pb-4 px-4">
                {showSaveButton && (
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleSave}
                    >
                        {t("Save")}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default ApiDataModal;